import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import { loadRegionResolver, normalizeText, type Region, type RegionResolver } from "@/lib/geo";
import type { AppUser, Seller, UserAddress } from "@/types";

/**
 * O resolvedor de região (ver `lib/geo.ts`), carregado uma vez por sessão.
 *
 * Os dados geográficos não mudam: nada de revalidar nem de descartar do cache.
 * Enquanto carregam, as telas mostram o texto cadastrado.
 */
export function useRegionResolver() {
  return useQuery({
    queryKey: queryKeys.geoResolver(),
    queryFn: loadRegionResolver,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** O endereço padrão do cliente, ou o primeiro: é ele que define a região. */
export function primaryAddress(addresses: UserAddress[] | undefined): UserAddress | undefined {
  return addresses?.find((a) => a.isDefault) ?? addresses?.[0];
}

/** A região do estabelecimento, pelo `location` gravado no cadastro dele. */
export function regionOfSeller(seller: Seller, resolver: RegionResolver | undefined): Region | null {
  if (!resolver) return null;
  return resolver.resolve({
    latitude: seller.location?.latitude,
    longitude: seller.location?.longitude,
    city: seller.address?.city,
    state: seller.address?.state,
  });
}

/** A região do cliente, pelo endereço padrão guardado no documento dele. */
export function regionOfClient(client: AppUser, resolver: RegionResolver | undefined): Region | null {
  if (!resolver) return null;
  const address = primaryAddress(client.addresses);
  if (!address) return null;
  return resolver.resolve({
    latitude: address.latitude,
    longitude: address.longitude,
    city: address.city,
    state: address.state,
  });
}

/**
 * A busca por texto também casa com a região resolvida: procurar "Santa Rita"
 * acha quem cadastrou "SRS", e procurar "Minas" acha quem cadastrou "MG".
 */
export function regionMatches(region: Region | null, needle: string): boolean {
  if (!region || !needle) return false;
  const key = normalizeText(needle);
  return [region.cityName, region.stateName, region.uf].some((value) =>
    normalizeText(value).includes(key),
  );
}
